import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  PersonSimpleRun,
  PersonSimpleWalk,
  Barbell,
  Scales,
  Bicycle,
  TrendUp,
  Heartbeat,
  Lightning,
  Trash,
  PencilSimple,
  Funnel,
  House,
  ListBullets,
  ChartLineUp,
  Medal
} from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cardio-journey-app/artifacts/tyj7q2ct_logo%20365.png";

const MACHINES = [
  { type: 'treadmill', label: 'Treadmill', Icon: PersonSimpleRun },
  { type: 'elliptical', label: 'Elliptical', Icon: Heartbeat },
  { type: 'stair_climber', label: 'Stair Climber', Icon: TrendUp },
  { type: 'row_machine', label: 'Row Machine', Icon: Barbell },
  { type: 'running', label: 'Running', Icon: Lightning },
  { type: 'speed_walking', label: 'Speed Walking', Icon: PersonSimpleWalk },
  { type: 'stationary_bike', label: 'Stationary Bike', Icon: Bicycle },
  { type: 'weekly_weight', label: 'Weekly Weight', Icon: Scales },
];

const History = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterMachine, setFilterMachine] = useState('all');
  const [filterDate, setFilterDate] = useState('');
  const [editingLog, setEditingLog] = useState(null);

  const fetchLogs = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = new URLSearchParams();
      if (filterMachine !== 'all') params.append('machine_type', filterMachine);
      if (filterDate) {
        params.append('start_date', filterDate);
        params.append('end_date', filterDate);
      }
      const res = await axios.get(`${API}/cardio-logs?${params.toString()}`, { withCredentials: true, headers });
      setLogs(res.data);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [token, filterMachine, filterDate]);

  const handleDelete = async (logId) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/cardio-logs/${logId}`, { withCredentials: true, headers });
      toast.success('Entry deleted');
      setLogs(logs.filter(l => l.log_id !== logId));
    } catch {
      toast.error('Failed to delete');
    }
  };

  const getMachineIcon = (type) => {
    const m = MACHINES.find(m => m.type === type);
    return m ? m.Icon : Lightning;
  };

  const getMachineLabel = (type) => {
    const m = MACHINES.find(m => m.type === type);
    return m ? m.label : type.replace(/_/g, ' ');
  };

  return (
    <div className="min-h-screen bg-background pb-24 relative overflow-hidden" data-testid="history-screen">
      {/* Watermark */}
      <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: `url(${LOGO_URL})`, backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundSize: '50%' }} />

      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border px-6 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-4">
          <button onClick={() => navigate('/dashboard')} className="w-10 h-10 rounded-full bg-card flex items-center justify-center" data-testid="history-back-btn">
            <ArrowLeft weight="bold" className="w-5 h-5 text-foreground" />
          </button>
          <h1 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>History</h1>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-6 py-5 space-y-4 relative z-10">
        {/* Filters */}
        <div className="premium-card p-4" data-testid="history-filters">
          <div className="flex items-center gap-2 mb-3">
            <Funnel weight="duotone" className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold">Filters</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Select value={filterMachine} onValueChange={setFilterMachine}>
              <SelectTrigger className="bg-background border-border rounded-xl" data-testid="filter-machine">
                <SelectValue placeholder="All Machines" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Machines</SelectItem>
                {MACHINES.filter(m => m.type !== 'weekly_weight').map(m => (
                  <SelectItem key={m.type} value={m.type}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="bg-background border-border rounded-xl"
              data-testid="filter-date"
            />
          </div>
          {(filterMachine !== 'all' || filterDate) && (
            <button
              onClick={() => { setFilterMachine('all'); setFilterDate(''); }}
              className="text-xs text-[#D4AF37] mt-2 hover:underline"
              data-testid="clear-filters"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Results count */}
        <p className="text-xs text-muted-foreground">{logs.length} {logs.length === 1 ? 'entry' : 'entries'} found</p>

        {/* Logs List */}
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-3 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : logs.length > 0 ? (
          <div className="space-y-2">
            {logs.map((log) => {
              const LogIcon = getMachineIcon(log.cardio_type);
              return (
                <div key={log.log_id} className="premium-card p-4" data-testid={`history-log-${log.log_id}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                      <LogIcon weight="duotone" className="w-4 h-4 text-[#D4AF37]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground">{getMachineLabel(log.cardio_type)}</p>
                      <p className="text-xs text-muted-foreground">{log.date}</p>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => navigate(`/tracker/${log.cardio_type}`, { state: { editLog: log } })}
                        className="w-8 h-8 rounded-lg bg-card flex items-center justify-center hover:bg-[#D4AF37]/10 transition-colors"
                        data-testid={`edit-${log.log_id}`}
                      >
                        <PencilSimple weight="bold" className="w-4 h-4 text-muted-foreground" />
                      </button>
                      <button
                        onClick={() => handleDelete(log.log_id)}
                        className="w-8 h-8 rounded-lg bg-card flex items-center justify-center hover:bg-red-500/10 transition-colors"
                        data-testid={`delete-${log.log_id}`}
                      >
                        <Trash weight="bold" className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </div>
                  {/* Details row */}
                  <div className="flex gap-4 text-xs text-muted-foreground ml-12">
                    {log.duration_minutes > 0 && <span>{log.duration_minutes} min</span>}
                    {log.distance && <span>{log.distance} mi</span>}
                    {log.calories_burned && <span>{log.calories_burned} cal</span>}
                    {log.start_time && log.end_time && <span>{log.start_time} - {log.end_time}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="premium-card p-8 text-center">
            <p className="text-muted-foreground">No entries found</p>
          </div>
        )}
      </main>

      {/* Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 bg-background/90 backdrop-blur-xl border-t border-border px-6 py-3 z-50">
        <div className="max-w-lg mx-auto flex items-center justify-around">
          <Link to="/dashboard" className="flex flex-col items-center gap-1 text-muted-foreground hover:text-foreground" data-testid="nav-home">
            <House weight="duotone" className="w-6 h-6" />
            <span className="text-[10px]">Home</span>
          </Link>
          <Link to="/history" className="flex flex-col items-center gap-1 text-[#D4AF37]" data-testid="nav-history">
            <ListBullets weight="fill" className="w-6 h-6" />
            <span className="text-[10px]">History</span>
          </Link>
          <Link to="/progress" className="flex flex-col items-center gap-1 text-muted-foreground hover:text-foreground" data-testid="nav-progress">
            <ChartLineUp weight="duotone" className="w-6 h-6" />
            <span className="text-[10px]">Progress</span>
          </Link>
          <Link to="/milestones" className="flex flex-col items-center gap-1 text-muted-foreground hover:text-foreground" data-testid="nav-milestones">
            <Medal weight="duotone" className="w-6 h-6" />
            <span className="text-[10px]">Badges</span>
          </Link>
        </div>
      </nav>
    </div>
  );
};

export default History;
